import { Header } from "@/app/layout/Header";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { classesApi } from "../api/client";
import {
  useBulkDeleteClassTemplates,
  useBulkToggleClassTemplateStatus,
  useClassTemplatesQuery,
  useDeleteClassTemplate,
} from "../api/queries";
import { ClassTemplateFilter } from "../ui/ClassTemplateFilter";
import { ClassTemplateTable } from "../ui/ClassTemplateTable";
import { ClassTemplateToolbar } from "../ui/ClassTemplateToolbar";

export function ClassTemplatesPage() {
  const nav = useNavigate({ from: "/app/classes/templates" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/classes/templates",
  });

  const query = useMemo<OffsetPageQueryDTO>(
    () => ({
      limit: search.limit ?? 20,
      offset: search.offset ?? 0,
      sort: search.sort,
      filters: search.filters,
    }),
    [search.limit, search.offset, search.sort, search.filters],
  );

  const list = useClassTemplatesQuery(query);
  const canCreate = useActionPermission(["classes:create"]);
  const canUpdate = useActionPermission(["classes:update"]);
  const canDelete = useActionPermission(["classes:delete"]);
  const deleteTemplate = useDeleteClassTemplate();
  const bulkDelete = useBulkDeleteClassTemplates();
  const bulkToggleStatus = useBulkToggleClassTemplateStatus();

  const handleBulkDelete = async (ids: string[]) => {
    await toast.promise(bulkDelete.run(ids), {
      loading: "ກຳລັງລຶບ...",
      success: (result) => `ລຶບ template ${result.deletedCount} ລາຍການສຳເລັດ`,
      error: "ລຶບ template ລົ້ມເຫຼວ",
    });
  };

  const handleBulkToggleStatus = async (
    ids: string[],
    status: "active" | "inactive",
  ) => {
    await toast.promise(bulkToggleStatus.run({ ids, status }), {
      loading: "ກຳລັງອັບເດດ...",
      success: (result) =>
        `ອັບເດດສະຖານະ template ${result.updatedCount} ລາຍການສຳເລັດ`,
      error: "ອັບເດດສະຖານະ template ລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Template ຄລາດສ໌" },
          ]}
        />
      </Header>

      <Main>
        <ClassTemplateToolbar
          canManage={!!canCreate}
          onCreate={() => nav({ to: "/app/classes/templates/create" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ClassTemplateFilter />

          <ClassTemplateTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(template) => {
              nav({
                to: "/app/classes/templates/$id",
                params: { id: template.id },
              });
            }}
            onEdit={(template) => {
              nav({
                to: "/app/classes/templates/$id/edit",
                params: { id: template.id },
              });
            }}
            onToggleStatus={async (id, status) => {
              try {
                await toast.promise(
                  classesApi.templates.toggleStatus(id, status),
                  {
                    loading: "ກຳລັງອັບເດດ...",
                    success: "ອັບເດດສະຖານະສຳເລັດ",
                    error: "ອັບເດດສະຖານະລົ້ມເຫຼວ",
                  },
                );
                list.refetch();
              } catch (error) {
                // Error handling is done by toast.promise
              }
            }}
            onDelete={async (id) => {
              toast.promise(deleteTemplate.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບ template ສຳເລັດ",
                error: "ລຶບ template ລົ້ມເຫຼວ",
              });
            }}
            onBulkDelete={canDelete ? handleBulkDelete : undefined}
            onBulkToggleStatus={canUpdate ? handleBulkToggleStatus : undefined}
          />
        </div>
      </Main>
    </>
  );
}
